import { useEffect, useState } from "react";
import { api } from "../api";
import { fmtTime } from "../format";

type Check = {
  name: string;
  category: string;
  status: "ok" | "degraded" | "down";
  detail: string;
  corrective_action: string;
};

type DiagnosticResult = {
  tenant_id: string;
  run_at: string;
  checks: Check[];
  summary: { ok: number; degraded: number; down: number };
};


// Every trade story this firm has published to a public URL. The tenant sees
// the FULL record — which trade, how many people read it, when it was last
// read — because it's their data and their exposure. (Helm, the platform
// side, deliberately sees only counts: same rule as graph memory.)
type Share = {
  id: string; token: string; url: string; symbol: string; book: string;
  label: string | null; created_at: string | null; revoked_at: string | null;
  view_count: number; last_viewed_at: string | null; active: boolean;
};

const CATEGORY_ORDER = ["governance", "decisions", "vendors", "venues", "feeds", "infra"];
const CATEGORY_TITLE: Record<string, string> = {
  governance: "Governance & Risk Gates",
  decisions: "Board & Desk Decisions",
  vendors: "LLM Vendors",
  venues: "Broker / Venues",
  feeds: "Data Feeds",
  infra: "Infrastructure",
};

const DOT: Record<Check["status"], string> = {
  ok: "bg-up", degraded: "bg-gold", down: "bg-down",
};
const LABEL: Record<Check["status"], string> = {
  ok: "OK", degraded: "Degraded", down: "Down",
};

export default function Control() {
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runDiagnostic() {
    setLoading(true);
    setError(null);
    try {
      setResult(await api.get<DiagnosticResult>("/api/diagnostics/run"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const grouped = result
    ? CATEGORY_ORDER.map((cat) => ({ cat, checks: result.checks.filter((c) => c.category === cat) }))
        .filter((g) => g.checks.length > 0)
    : [];

  const overall = result
    ? result.summary.down > 0 ? "down" : result.summary.degraded > 0 ? "degraded" : "ok"
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-bold">Control Panel</h1>
          <p className="text-sm text-slate-400">
            Live status of every access point and operational mechanism — brokers, LLM vendors, data feeds,
            infrastructure, and the governance/decision mechanisms that gate real trades. Every failed or
            degraded check comes with a corrective action.
          </p>
        </div>
        <button className="btn btn-primary" onClick={runDiagnostic} disabled={loading}>
          {loading ? "Running full diagnostic…" : "Run full diagnostic"}
        </button>
      </div>

      {error && <div className="card text-down text-sm">{error}</div>}

      {result && (
        <div className="card flex items-center gap-4">
          <span className={`w-3 h-3 rounded-full ${overall && DOT[overall]}`} />
          <span className="font-semibold text-sm">
            {overall === "ok" ? "All systems go" : overall === "degraded" ? "Some systems degraded" : "Action needed"}
          </span>
          <span className="text-xs text-slate-500">
            {result.summary.ok} ok · {result.summary.degraded} degraded · {result.summary.down} down
          </span>
          <span className="text-xs text-slate-600 ml-auto">as of {fmtTime(result.run_at)}</span>
        </div>
      )}

      {!result && !loading && (
        <div className="card text-sm text-slate-400">
          No diagnostic has been run yet this session. Click "Run full diagnostic" — it live-checks every
          broker, LLM vendor, data feed, and governance mechanism (a few seconds).
        </div>
      )}

      <PublishedStories />

      {grouped.map(({ cat, checks }) => (
        <div key={cat} className="card space-y-2">
          <h2 className="text-sm font-semibold text-slate-300">{CATEGORY_TITLE[cat] ?? cat}</h2>
          <div className="divide-y divide-edge">
            {checks.map((c) => (
              <div key={c.name} className="py-2 flex items-start gap-3">
                <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${DOT[c.status]}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium">{c.name}</span>
                    <span className="text-xs text-slate-500">{LABEL[c.status]}</span>
                  </div>
                  <div className="text-xs text-slate-400 break-words">{c.detail}</div>
                  {c.corrective_action && (
                    <div className="text-xs text-gold mt-0.5 break-words">→ {c.corrective_action}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PublishedStories() {
  const [rows, setRows] = useState<Share[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    try { setRows(await api.get<Share[]>("/api/performance/trade/story/shares")); }
    catch { setRows([]); }
  }
  useEffect(() => { void load(); }, []);

  async function revoke(id: string) {
    setBusy(id);
    try { await api.del(`/api/performance/trade/story/share/${id}`); await load(); }
    catch { /* leave as-is */ }
    finally { setBusy(null); }
  }

  const active = (rows ?? []).filter((r) => r.active);
  const views = (rows ?? []).reduce((a, r) => a + (r.view_count || 0), 0);

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-300">Published stories</h2>
        <p className="text-xs text-slate-500">
          Trade stories you’ve put on a public URL. Anyone with the link can read that one trade —
          its size, prices and P&amp;L. Revoking takes it down immediately.
        </p>
      </div>

      {rows === null && <div className="text-xs text-slate-500">loading…</div>}
      {rows !== null && rows.length === 0 && (
        <div className="text-xs text-slate-500">
          Nothing published. Open any trade’s Story and hit “Share publicly” to create a link.
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="flex gap-5 text-xs">
            <span className="text-slate-400">
              <span className="text-slate-100 font-bold">{active.length}</span> live
            </span>
            <span className="text-slate-400">
              <span className="text-slate-100 font-bold">{rows.length - active.length}</span> revoked
            </span>
            <span className="text-slate-400">
              <span className="text-accent font-bold">{views}</span> total reads
            </span>
          </div>
          {/* overflow wrapper: Kaizen D3/F4 — this table was clipped
              unrecoverable on phones under the frame's overflow-x-hidden. */}
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase">
                <th className="py-1">Trade</th><th>Book</th><th>Published</th>
                <th className="text-right">Reads</th><th>Last read</th><th></th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-edge ${r.active ? "" : "opacity-50"}`}>
                  <td className="py-1.5 font-bold">{r.symbol}</td>
                  <td className="text-slate-400">{r.book}</td>
                  <td className="text-slate-500">{r.created_at ? fmtTime(r.created_at) : "—"}</td>
                  <td className={`text-right ${r.view_count ? "text-accent" : "text-slate-600"}`}>
                    {r.view_count}
                  </td>
                  <td className="text-slate-500">{r.last_viewed_at ? fmtTime(r.last_viewed_at) : "never"}</td>
                  <td className="text-right">
                    {r.active ? (
                      <button className="btn text-[10px]" disabled={busy === r.id}
                              onClick={() => revoke(r.id)}>
                        {busy === r.id ? "…" : "Revoke"}
                      </button>
                    ) : (
                      <span className="text-slate-600 font-sans">revoked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {active.length > 0 && (
            <div className="space-y-0.5">
              {active.map((r) => (
                <div key={r.id} className="font-mono text-[10px] text-slate-500 break-all">{r.url}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
